<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAcesso } from 'dashboard/composables/useAcesso';
import {
  CONVERSATION_PERMISSIONS,
  ESCOPOS_DA_CONVERSA,
} from 'dashboard/routes/dashboard/conversation/conversation.routes';
import Icon from 'next/icon/Icon.vue';
import ProductUsageOverview from './components/ProductUsageOverview.vue';

const { t } = useI18n();
const { accountId, accountScopedRoute } = useAccount();
const { checkPermissions } = usePolicy();
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
// ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, os cheques abaixo pedem o escopo da tela que leem ou a que
// levam; desligada, o de hoje.
const { decide } = useAcesso();

// A visão geral consome reports v2 e analytics, ambos admin-only no backend.
// Para agente/moderator a seção não entra em tela: meia tela de 403 seria pior
// do que a home continuar sendo só o launcher. Com a chave ligada, os números que ela lê
// (`reports#summary`, `atendimento#comparar`) são do `reports:read`.
const canSeeOverview = computed(() =>
  decide('reports:read', () => currentRole.value === 'administrator')
);

// The registry (account_apps) stores slug/name/url only — icon and blurb are a
// shell-side concern. Unknown slugs fall back to a neutral icon and no blurb
// rather than an invented description.
const APP_ICONS = {
  expclub: 'i-lucide-ticket',
  powercare: 'i-lucide-heart-pulse',
  powercoach: 'i-lucide-graduation-cap',
  powerinsights: 'i-lucide-chart-spline',
  powerretail: 'i-lucide-shopping-bag',
};

const apps = ref([]);
const loading = ref(true);
const failed = ref(false);

const appDescription = slug => {
  const key = `OS_HOME.APP_DESCRIPTIONS.${slug.toUpperCase()}`;
  const description = t(key);
  return description === key ? '' : description;
};

// PowerChat is the shell itself, not a registry row. It still has to earn its
// card: the same permissions the conversations route enforces, or the card
// would just bounce the user back here. Com a chave ligada, o mesmo escopo da rota.
const powerchatCard = computed(() => {
  if (
    !decide(ESCOPOS_DA_CONVERSA, () =>
      checkPermissions(CONVERSATION_PERMISSIONS)
    )
  )
    return null;
  return {
    key: 'powerchat',
    name: t('OS_HOME.POWERCHAT.NAME'),
    description: t('OS_HOME.POWERCHAT.DESCRIPTION'),
    icon: 'i-lucide-message-circle',
    to: accountScopedRoute('home'),
  };
});

const cards = computed(() => [
  ...(powerchatCard.value ? [powerchatCard.value] : []),
  ...apps.value.map(app => ({
    key: app.slug,
    name: app.name,
    description: appDescription(app.slug),
    icon: APP_ICONS[app.slug] || 'i-lucide-layout-grid',
    to: accountScopedRoute('powerapp', { slug: app.slug }),
  })),
]);

onMounted(async () => {
  try {
    const { data } = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/powerapps`
    );
    apps.value = data;
  } catch {
    failed.value = true;
  }
  loading.value = false;
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 bg-n-background">
    <main class="overflow-auto flex-1 px-4 py-6 sm:px-6 sm:py-8">
      <div class="flex flex-col gap-8 mx-auto w-full max-w-5xl">
        <div>
          <i18n-t
            keypath="OS_HOME.WELCOME"
            tag="h1"
            class="text-xl font-semibold sm:text-2xl text-n-slate-12"
          >
            <template #brand>
              <span
                class="text-transparent bg-clip-text bg-gradient-to-r from-[rgb(var(--pz-brand-rgb))] to-[#7b5cff]"
              >
                {{ t('OS_HOME.BRAND') }}
              </span>
            </template>
            <template #name>{{ currentUser.available_name }}</template>
          </i18n-t>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('OS_HOME.SUBTITLE') }}
          </p>
        </div>

        <section class="flex flex-col gap-4">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('OS_HOME.APPS_TITLE') }}
          </h2>

          <p v-if="failed" class="text-sm text-n-ruby-11">
            {{ t('OS_HOME.LOAD_ERROR') }}
          </p>

          <div class="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2">
            <router-link
              v-for="card in cards"
              :key="card.key"
              :to="card.to"
              class="flex gap-3 items-start p-4 no-underline sm:gap-4 sm:p-5 border transition-all duration-150 rounded-pz border-n-weak bg-n-card shadow-pz hover:border-n-brand hover:shadow-pz-hover hover:-translate-y-px"
            >
              <span
                class="grid flex-shrink-0 place-content-center rounded-lg size-10 bg-pz-brand-tint text-pz-brand-on-tint"
              >
                <Icon :icon="card.icon" class="size-5" />
              </span>
              <span class="min-w-0">
                <span class="block text-base font-medium text-n-slate-12">
                  {{ card.name }}
                </span>
                <span
                  v-if="card.description"
                  class="block mt-1 text-sm leading-relaxed text-n-slate-11"
                >
                  {{ card.description }}
                </span>
              </span>
            </router-link>
          </div>

          <p v-if="loading" class="text-sm text-n-slate-11">
            {{ t('OS_HOME.LOADING') }}
          </p>
          <p
            v-else-if="!cards.length && !failed"
            class="text-sm leading-relaxed text-n-slate-11"
          >
            {{ t('OS_HOME.EMPTY') }}
          </p>
        </section>

        <ProductUsageOverview v-if="canSeeOverview" :app-count="apps.length" />
      </div>
    </main>
  </div>
</template>
